import React, {useState, useEffect} from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import {usePermissions} from '../../utils/usePermissions';
import DetailDemandesDevis from './DetailDemandesDevis';
import './DemandesDevis.css';

const API_URL = import.meta.env.VITE_API_URL;

const DemandesDevis = ({onNavigate, onLogout, currentPage}) => {
  const {hasPermission} = usePermissions();
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtreStatut, setFiltreStatut] = useState('');
  const [statistiques, setStatistiques] = useState({
    total: 0,
    enAttente: 0,
    validees: 0,
    rejetees: 0
  });
  const [selectedDemande, setSelectedDemande] = useState(null);
  const [showDetailPage, setShowDetailPage] = useState(false);

  useEffect(() => {
    fetchDemandes();
    fetchStatistiques();
  }, [filtreStatut]);

  const fetchDemandes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const queryParams = filtreStatut ? `?statut=${filtreStatut}` : '';
      const response = await axios.get(`${API_URL}/demandes-devis${queryParams}`, {
        headers: {Authorization: `Bearer ${token}`}
      });
      setDemandes(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des demandes:', error);
      Swal.fire('Erreur', 'Impossible de charger les demandes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistiques = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/demandes-devis/statistiques/all`, {
        headers: {Authorization: `Bearer ${token}`}
      });
      setStatistiques(response.data);
    } catch (error) {
      console.error('Erreur statistiques:', error);
    }
  };

  const getStatutColor = (statut) => {
    const colors = {
      'en_cours': '#17a2b8',
      'en_attente': '#ffc107',
      'validé': '#28a745',
      'rejeté': '#dc3545'
    };
    return colors[statut] || '#6c757d';
  };

  const getStatutLabel = (statut) => {
    const labels = {
      'en_cours': 'En cours',
      'en_attente': 'En attente',
      'validé': 'Validé',
      'rejeté': 'Rejeté'
    };
    return labels[statut] || statut;
  };

  const handleViewDetail = (demande) => {
    setSelectedDemande(demande);
    setShowDetailPage(true);
  };

  const handleBackFromDetail = () => {
    setShowDetailPage(false);
    setSelectedDemande(null);
    fetchDemandes();
    fetchStatistiques();
  };

  const handleValider = async (demandeId) => {
    const {value: commentaire} = await Swal.fire({
      title: 'Valider la demande',
      input: 'textarea',
      inputLabel: 'Commentaire (optionnel)',
      inputPlaceholder: 'Entrez un commentaire pour le client...',
      showCancelButton: true,
      confirmButtonText: 'Valider',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#28a745'
    });

    if (commentaire !== undefined) {
      try {
        const token = localStorage.getItem('token');
        await axios.put(
          `${API_URL}/demandes-devis/${demandeId}/valider`,
          {commentaireAdmin: commentaire},
          {headers: {Authorization: `Bearer ${token}`}}
        );
        Swal.fire('Succès', 'Demande validée avec succès', 'success');
        fetchDemandes();
        fetchStatistiques();
      } catch (error) {
        console.error('Erreur validation:', error);
        Swal.fire('Erreur', 'Impossible de valider la demande', 'error');
      }
    }
  };

  const handleRejeter = async (demandeId) => {
    const {value: commentaire} = await Swal.fire({
      title: 'Rejeter la demande',
      input: 'textarea',
      inputLabel: 'Motif du rejet',
      inputPlaceholder: 'Entrez le motif du rejet...',
      showCancelButton: true,
      confirmButtonText: 'Rejeter',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#dc3545',
      inputValidator: (value) => {
        if (!value) return 'Vous devez entrer un motif !';
      }
    });

    if (commentaire) {
      try {
        const token = localStorage.getItem('token');
        await axios.put(
          `${API_URL}/demandes-devis/${demandeId}/rejeter`,
          {commentaireAdmin: commentaire},
          {headers: {Authorization: `Bearer ${token}`}}
        );
        Swal.fire('Succès', 'Demande rejetée', 'success');
        fetchDemandes();
        fetchStatistiques();
      } catch (error) {
        console.error('Erreur rejet:', error);
        Swal.fire('Erreur', 'Impossible de rejeter la demande', 'error');
      }
    }
  };

  const handleSupprimer = async (demandeId) => {
    const result = await Swal.fire({
      title: 'Êtes-vous sûr ?',
      text: 'Cette action est irréversible',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#dc3545'
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API_URL}/demandes-devis/${demandeId}`, {
          headers: {Authorization: `Bearer ${token}`}
        });
        Swal.fire('Supprimé', 'Demande supprimée avec succès', 'success');
        fetchDemandes();
        fetchStatistiques();
      } catch (error) {
        console.error('Erreur suppression:', error);
        Swal.fire('Erreur', 'Impossible de supprimer la demande', 'error');
      }
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ── Si une demande est sélectionnée, afficher la page de détail complète ──
  if (showDetailPage && selectedDemande) {
    return (
      <DetailDemandesDevis
        demande={selectedDemande}
        onBack={handleBackFromDetail}
        onNavigate={onNavigate}
        onLogout={onLogout}
        currentPage={currentPage}
      />
    );
  }

  return (
    <div className="demandes-management-container">
      <BackOfficeMenu
        onNavigate={onNavigate}
        onLogout={onLogout}
        currentPage={currentPage}
      />

      <div className="demandes-management-content">
        <div className="demandes-header">
          <div>
            <h1>Gestion des Demandes de Devis</h1>
            <p>Suivez et gérez toutes les demandes de devis clients</p>
          </div>
        </div>

        {/* Statistiques */}
        <div className="stats-grid-v2">
          <div className="stat-card-v2 stat-v2-total">
            <div className="stat-v2-top">
              <span className="stat-v2-icon">📋</span>
              <span className="stat-v2-label">Total</span>
            </div>
            <div className="stat-v2-number">{statistiques.total}</div>
            <div className="stat-v2-foot">Toutes demandes</div>
          </div>

          <div className="stat-card-v2 stat-v2-attente">
            <div className="stat-v2-top">
              <span className="stat-v2-icon">⏳</span>
              <span className="stat-v2-label">En Attente</span>
            </div>
            <div className="stat-v2-number">{statistiques.enAttente}</div>
            <div className="stat-v2-foot">En cours de traitement</div>
          </div>

          <div className="stat-card-v2 stat-v2-validees">
            <div className="stat-v2-top">
              <span className="stat-v2-icon">✅</span>
              <span className="stat-v2-label">Validées</span>
            </div>
            <div className="stat-v2-number">{statistiques.validees}</div>
            <div className="stat-v2-foot">Demandes acceptées</div>
          </div>

          <div className="stat-card-v2 stat-v2-rejetees">
            <div className="stat-v2-top">
              <span className="stat-v2-icon">❌</span>
              <span className="stat-v2-label">Rejetées</span>
            </div>
            <div className="stat-v2-number">{statistiques.rejetees}</div>
            <div className="stat-v2-foot">Demandes refusées</div>
          </div>
        </div>


        {/* Filtres */}
        <div className="filters-bar">
          <div className="filter-group">
            <label>Filtrer par statut :</label>
            <select value={filtreStatut} onChange={(e) => setFiltreStatut(e.target.value)}>
              <option value="">Tous</option>
              <option value="en_attente">En attente</option>
              <option value="validé">Validé</option>
              <option value="rejeté">Rejeté</option>
            </select>
          </div>
          <button className="btn-reset-filter" onClick={() => setFiltreStatut('')}>
            Réinitialiser
          </button>
        </div>

        {/* Liste des demandes */}
        {loading ? (
          <LoadingSpinner message="Chargement des demandes de devis..." />
        ) : demandes.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h3>Aucune demande de devis</h3>
            <p>Les demandes apparaîtront ici</p>
          </div>
        ) : (
          <div className="demandes-table-container">
            <table className="demandes-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Client</th>
                  <th>Email</th>
                  <th>Consommation</th>
                  <th>Puissance</th>
                  <th>Type Contrat</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {demandes.map((demande) => (
                  <tr key={demande._id}>
                    <td>{formatDate(demande.createdAt)}</td>
                    <td className="client-namee">
                      <strong>{demande.nom} {demande.prenom}</strong>
                    </td>
                    <td>{demande.mail}</td>
                    <td>
                      <div className="consumption-info">
                        <span className="consumption-value">{demande.consommationAnnuelle} kWh/an</span>
                        {demande.pompage && (
                          <small className="pompage-indicator">+ Pompage</small>
                        )}
                      </div>
                    </td>
                    <td>
                      <strong>{demande.puissanceCommerciale} kWc</strong>
                    </td>
                    <td>
                      <span className={`contract-badge ${demande.typeContrat === 'credit_prosol' ? 'prosol' : 'hors'}`}>
                        {demande.typeContrat === 'credit_prosol' ? 'Crédit PROSOL' : 'Hors PROSOL'}
                      </span>
                    </td>
                    <td>
                      <span
                        className="statut-badge"
                        style={{backgroundColor: getStatutColor(demande.statut)}}
                      >
                        {getStatutLabel(demande.statut)}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <button
                        className="btn-action btn-view"
                        onClick={() => handleViewDetail(demande)}
                        title="Voir détails"
                      >
                        👁️
                      </button>
                      {demande.statut === 'en_attente' && hasPermission('devis', 'update') && (
                        <>
                          <button
                            className="btn-action btn-validate"
                            onClick={() => handleValider(demande._id)}
                            title="Valider"
                          >
                            ✓
                          </button>
                          <button
                            className="btn-action btn-reject"
                            onClick={() => handleRejeter(demande._id)}
                            title="Rejeter"
                          >
                            ✗
                          </button>
                        </>
                      )}
                      {hasPermission('devis', 'delete') && (
                        <button
                          className="btn-action btn-delete"
                          onClick={() => handleSupprimer(demande._id)}
                          title="Supprimer"
                        >
                          🗑️
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemandesDevis;
